import { useEffect, useState } from "react";
import { Plus, ArrowLeft } from "lucide-react";
import { API, errMsg, TOPIC_NAMES, fmtDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { GuideContent } from "../../components/GuideContent";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground",
  published: "bg-success text-success-foreground",
  retired: "bg-destructive/10 text-destructive",
};

export default function AdminGuides() {
  const [guides, setGuides] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | guide object
  const [form, setForm] = useState({ title: "", topic: "cardiology", body: "" });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const FORMATS = [
    { key: "title", label: "Title" },
    { key: "subtitle", label: "Subtitle" },
    { key: "bold", label: "Bold" },
    { key: "red", label: "Red" },
    { key: "green", label: "Green" },
    { key: "brown", label: "Brown" },
  ];

  const applyFormat = (type) => {
    const ta = document.getElementById("g-body");
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const val = form.body;
    const sel = val.slice(s, e) || "text";
    let next;
    let cursor;
    if (type === "title" || type === "subtitle") {
      const prefix = type === "title" ? "# " : "## ";
      const lineStart = val.lastIndexOf("\n", s - 1) + 1;
      next = val.slice(0, lineStart) + prefix + val.slice(lineStart);
      cursor = s + prefix.length;
    } else if (type === "bold") {
      next = val.slice(0, s) + "**" + sel + "**" + val.slice(e);
      cursor = e + 4;
    } else {
      next = val.slice(0, s) + `[${type}]` + sel + `[/${type}]` + val.slice(e);
      cursor = e + type.length * 2 + 5;
    }
    setForm({ ...form, body: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(cursor, cursor); }, 0);
  };

  const load = async () => {
    try {
      const { data } = await API.get(`/admin/guides${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`);
      setGuides(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const startNew = () => { setForm({ title: "", topic: "cardiology", body: "" }); setEditing("new"); };
  const startEdit = (g) => { setForm({ title: g.title, topic: g.topic, body: g.body }); setEditing(g); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await API.post("/admin/guides", form);
        toast.success("Guide created as draft");
      } else {
        await API.put(`/admin/guides/${editing._id}`, form);
        toast.success("Guide saved");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const setStatus = async (g, status) => {
    try {
      await API.post(`/admin/guides/${g._id}/status`, { status });
      toast.success(`"${g.title}" → ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (editing !== null) {
    return (
      <div data-testid="guide-editor" className="max-w-3xl">
        <button data-testid="editor-back-btn" onClick={() => setEditing(null)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> All guides
        </button>
        <h1 className="font-display text-2xl font-bold mb-6" data-testid="guide-editor-title">
          {editing === "new" ? "New revision guide" : `Edit: ${editing.title}`}
        </h1>
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="g-title">Title</Label>
              <Input id="g-title" data-testid="guide-title-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Acute coronary syndrome — essentials" />
            </div>
            <div className="space-y-2">
              <Label>Topic</Label>
              <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                <SelectTrigger data-testid="guide-topic-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TOPIC_NAMES).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`guide-topic-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="g-body">Guide content</Label>
              <button type="button" data-testid="guide-preview-toggle" onClick={() => setPreview(!preview)}
                className="text-xs font-medium text-primary hover:underline">
                {preview ? "Back to editing" : "Preview"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="format-toolbar">
              {FORMATS.map((f) => (
                <button key={f.key} type="button" data-testid={`fmt-${f.key}`} onClick={() => applyFormat(f.key)}
                  className={`px-2.5 py-1 text-xs font-semibold border rounded transition-colors ${
                    f.key === "red" ? "border-destructive/40 text-destructive hover:bg-destructive/10"
                    : f.key === "green" ? "border-success/40 text-success hover:bg-success/10"
                    : "border-border hover:bg-muted"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {preview ? (
              <div className="border border-border rounded-md p-5 min-h-[300px] bg-background" data-testid="guide-preview">
                <GuideContent body={form.body} />
              </div>
            ) : (
              <>
                <Textarea id="g-body" data-testid="guide-body-input" rows={18} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Write the revision guide here. Select text and use the toolbar to format it." />
                <p className="text-xs text-muted-foreground">
                  Formatting: # Title · ## Subtitle · **bold** · [red]…[/red] · [green]…[/green] · [brown]…[/brown] · - bullet · 1. numbered list
                </p>
              </>
            )}
          </div>
          <div className="flex gap-3">
            <Button data-testid="guide-save-btn" onClick={save}
              disabled={saving || form.title.trim().length < 3 || form.body.trim().length < 10}>
              {saving ? "Saving…" : editing === "new" ? "Create as draft" : "Save changes"}
            </Button>
            <Button variant="outline" data-testid="guide-cancel-btn" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-guides-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-2xl font-bold" data-testid="admin-guides-title">Revision guides</h1>
        <div className="flex gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="guides-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["draft", "published", "retired"].map((s) => (
                <SelectItem key={s} value={s} data-testid={`guides-filter-${s}`} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" data-testid="new-guide-btn" onClick={startNew}>
            <Plus className="w-4 h-4 mr-1.5" /> New guide
          </Button>
        </div>
      </div>

      {!guides ? <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>
        : guides.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground" data-testid="guides-admin-empty">
            No guides yet. Create your first revision guide.
          </p>
        ) : (
          <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="admin-guides-list">
            {guides.map((g) => (
              <div key={g._id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`guide-row-${g._id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[g.status]}`}
                      data-testid={`guide-status-${g._id}`}>{g.status}</span>
                    <span className="text-xs text-muted-foreground">{TOPIC_NAMES[g.topic] || g.topic}</span>
                    <span className="text-xs text-muted-foreground">· {fmtDate(g.created_at)}</span>
                  </div>
                  <p className="text-sm font-medium">{g.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{g.body.slice(0, 100)}…</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" data-testid={`guide-edit-${g._id}`} onClick={() => startEdit(g)}>Edit</Button>
                  {g.status !== "published" && (
                    <Button size="sm" data-testid={`guide-publish-${g._id}`} onClick={() => setStatus(g, "published")}>Publish</Button>
                  )}
                  {g.status === "published" && (
                    <Button variant="outline" size="sm" data-testid={`guide-unpublish-${g._id}`} onClick={() => setStatus(g, "draft")}>Unpublish</Button>
                  )}
                  {g.status !== "retired" && (
                    <Button variant="ghost" size="sm" data-testid={`guide-retire-${g._id}`} onClick={() => setStatus(g, "retired")}>Retire</Button>
                  )}
                  {g.status === "retired" && (
                    <Button variant="outline" size="sm" data-testid={`guide-restore-${g._id}`} onClick={() => setStatus(g, "draft")}>Restore</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
